import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2, Loader } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ChatBot = () => {
  const { language } = useStore();
  const t = translations[language];
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = language === 'fr'
        ? "Bonjour ! 🐴 Je suis l'assistant EBB-SHOP. Comment puis-je vous aider aujourd'hui ? Je peux vous conseiller sur nos vêtements équestres, les tailles, ou répondre à vos questions."
        : "Hallo! 🐴 Ik ben de EBB-SHOP assistent. Hoe kan ik u vandaag helpen? Ik kan u adviseren over onze ruiterkleding, maten, of uw vragen beantwoorden.";
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [isOpen, language]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          language: language,
        }),
      });

      if (!response.ok) throw new Error('Chat error');

      const data = await response.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = language === 'fr'
        ? "Désolé, une erreur s'est produite. Veuillez réessayer."
        : "Sorry, er is een fout opgetreden. Probeer het opnieuw.";
      setMessages((prev) => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    // Re-add greeting
    const greeting = language === 'fr'
      ? "Bonjour ! 🐴 Je suis l'assistant EBB-SHOP. Comment puis-je vous aider aujourd'hui ?"
      : "Hallo! 🐴 Ik ben de EBB-SHOP assistent. Hoe kan ik u vandaag helpen?";
    setMessages([{ role: 'assistant', content: greeting }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 w-14 h-14 bg-amber-700 hover:bg-amber-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all z-[9999] ${isOpen ? 'hidden' : ''}`}
        data-testid="chat-button"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-lg shadow-2xl flex flex-col z-[9999] animate-slideUp" data-testid="chat-window">
          {/* Header */}
          <div className="bg-amber-700 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xl">🐴</span>
              </div>
              <div>
                <h3 className="font-semibold">
                  {language === 'fr' ? 'Assistant EBB-SHOP' : 'EBB-SHOP Assistent'}
                </h3>
                <p className="text-xs text-amber-200">
                  {language === 'fr' ? 'Conseiller équestre' : 'Ruiter adviseur'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={clearChat}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                title={language === 'fr' ? 'Effacer' : 'Wissen'}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-amber-700 text-white rounded-br-none'
                      : 'bg-gray-100 text-gray-800 rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 rounded-lg rounded-bl-none">
                  <Loader className="w-5 h-5 animate-spin text-amber-700" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === 'fr' ? 'Posez votre question...' : 'Stel uw vraag...'}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-amber-700 hover:bg-amber-800 disabled:bg-gray-300 text-white p-2 rounded-lg transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {language === 'fr' ? 'Propulsé par IA' : 'Aangedreven door AI'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
